/**
 * Image Model Compatibility Registry
 *
 * The model repository is the source of truth for image model metadata,
 * topology support, prompt enhancement workflow IDs, reference contracts,
 * endpoints, and execution routes. This module preserves the legacy
 * ImageControls-facing shape while deriving it from repository entries.
 */

import {
  MODEL_REPOSITORY,
  ModelFamily,
  ModelSurface,
  ModelTopology,
  ReferenceTransportStrategy,
} from './models';
import { loadFalExport } from '../services/fal/loaders';
import { getModelById } from '../services/models/repository';
import { promptCharBudgetFor } from '../services/creative-bible/resolver';
import { getElementReferenceWeight } from '../services/visual-inputs/elementReferenceWeight';
import {
  SCENE_ASSET_REFERENCE_BUCKET_KINDS,
  getSceneAssetReferenceBucketKind,
} from '../types/scene-assets/sceneAssetKinds';

const DEFAULT_IMAGE_MODEL_KEY = 'nano-banana-2-flash';

function isEnabled(feature) {
  if (feature == null || feature === false) return false;
  if (feature === true) return true;
  if (Array.isArray(feature)) return feature.length > 0;
  if (typeof feature === 'object') return feature.enabled !== false;
  return Boolean(feature);
}

function getOptions(feature) {
  return Array.isArray(feature?.options) ? [...feature.options] : null;
}

function getImageUi(model) {
  return model.ui?.image || {};
}

function getRouteExecution(model, mode) {
  const imageUi = getImageUi(model);
  const override = mode === 'generate'
    ? imageUi.generateExecution
    : imageUi.editExecution;
  if (override) return override;

  const topology = mode === 'generate'
    ? ModelTopology.TEXT_TO_IMAGE
    : ModelTopology.IMAGE_TO_IMAGE;
  return model.routes?.[topology]?.execution || null;
}

function createFalEndpointServiceLoader(execution) {
  const endpoint = typeof execution.endpoint === 'string' ? execution.endpoint.trim() : '';
  if (!endpoint) return undefined;

  return async () => {
    const generate = await loadFalExport('genericEndpoint', execution.methodName);
    return (params = {}) => generate({ ...params, endpoint });
  };
}

function createServiceLoader(model, mode) {
  const execution = getRouteExecution(model, mode);
  if (!execution) return undefined;

  if (execution.type === 'fal-service-export') {
    if (!execution.serviceKey || !execution.exportName) return undefined;
    return () => loadFalExport(execution.serviceKey, execution.exportName);
  }

  if (execution.type === 'fal-endpoint') {
    if (!execution.methodName) return undefined;
    return createFalEndpointServiceLoader(execution);
  }

  if (execution.type === 'provider-service-export') {
    if (!execution.providerKey || !execution.exportName) return undefined;
    // Dynamic import keeps the provider loader registry (browser/vitest
    // only) out of the plain-Node import graph this module lives in.
    return async () => {
      const { loadProviderExport } = await import('../services/providers/providerServiceLoaders.js');
      return loadProviderExport(execution.providerKey, execution.exportName);
    };
  }

  return undefined;
}

function createExecutionDescriptor(model, mode) {
  const execution = getRouteExecution(model, mode);
  return execution ? { ...execution } : null;
}

function mapImageCapabilities(model) {
  const features = model.features || {};
  const promptImages = features.promptImages || {};
  const imageUi = getImageUi(model);
  const maxOutputGenerate = features.batch?.max ?? 1;
  const minOutputGenerate = features.batch?.min ?? 1;
  const maxOutputEdit = imageUi.maxOutputEdit ?? maxOutputGenerate;

  return {
    maxInputImages: {
      generate: promptImages.generateMax ?? 0,
      edit: promptImages.editMax ?? promptImages.generateMax ?? 0,
    },
    maxOutputImages: {
      generate: maxOutputGenerate,
      edit: maxOutputEdit,
    },
    minOutputImages: {
      generate: minOutputGenerate,
      edit: Math.min(minOutputGenerate, maxOutputEdit),
    },
    aspectRatios: getOptions(features.aspectRatio),
    resolutions: getOptions(features.resolution),
    defaultResolution: features.resolution?.default,
    numericDials: features.midjourneyDials || null,
    styleRef: features.midjourneyStyleRef || null,
    version: features.midjourneyVersion || null,
    supportsBatchSelection: maxOutputGenerate === 1 || maxOutputGenerate > minOutputGenerate,
    hasNegativePrompt: isEnabled(features.negativePrompt),
    hasGuidanceScale: isEnabled(features.guidanceScale),
    hasInferenceSteps: isEnabled(features.inferenceSteps),
    supportsBoardPresets: isEnabled(features.boardPresets),
    supportsReferenceNarration: isEnabled(features.referenceNarration),
  };
}

function deriveReferenceLayout(model) {
  const references = model.references || {};
  const strategy = references.transport?.strategy;
  const buckets = references.transport?.buckets || {};
  const limits = references.limits || {};

  if (strategy === ReferenceTransportStrategy.ORDERED_BUCKETS) {
    return {
      strategy: 'ordered-buckets',
      buckets,
      totalMax: limits.total ?? 0,
    };
  }

  if (strategy === ReferenceTransportStrategy.PROMPT_TAGS) {
    return {
      strategy: 'positional-prompt-injection',
      buckets,
      totalMax: limits.total ?? 0,
      promptInjection: true,
    };
  }

  if (strategy === ReferenceTransportStrategy.SOURCE_SLOT) {
    const bucketKind = references.acceptedKinds?.includes('character')
      ? 'character'
      : 'prop';
    return {
      strategy: bucketKind === 'character' ? 'single-character' : 'source-slot',
      buckets: {
        [bucketKind]: {
          max: limits[bucketKind] ?? limits.total ?? 1,
          order: 0,
        },
      },
      totalMax: limits.total ?? 1,
    };
  }

  return {
    strategy: 'flat',
    buckets,
    totalMax: mapImageCapabilities(model).maxInputImages.edit,
  };
}

function mapRepositoryImageModel(model) {
  const imageUi = getImageUi(model);
  const referenceLayout = deriveReferenceLayout(model);
  const supportsStructuredReferences = referenceLayout.strategy !== 'flat';

  return {
    key: model.id,
    // The registry's provider: whose API a paid job runs on, and where recovery resumes it.
    provider: model.provider,
    editKey: imageUi.editKey || `${model.id}-edit`,
    label: model.label,
    qualityTier: model.qualityTier,
    attachedImageMode: imageUi.attachedImageMode || 'edit',
    requiresPromptImages: Boolean(imageUi.requiresPromptImages),
    enhancementKey: model.prompt?.enhancerId || null,
    editEnhancementKey: model.prompt?.editEnhancerId || model.prompt?.enhancerId || null,
    iconKey: model.iconKey,
    cost: model.cost,
    supportsCharacterReferences: Boolean(imageUi.supportsCharacterReferences || supportsStructuredReferences),
    supportsProps: Boolean(imageUi.supportsProps),
    creativeAdapter: imageUi.creativeAdapter || 'image-text-basic',
    service: {
      generate: createServiceLoader(model, 'generate'),
      edit: createServiceLoader(model, 'edit'),
    },
    serviceExecution: {
      generate: createExecutionDescriptor(model, 'generate'),
      edit: createExecutionDescriptor(model, 'edit'),
    },
    capabilities: mapImageCapabilities(model),
  };
}

function isImageGenerateModel(model) {
  return Boolean(
    model
    && model.family === ModelFamily.IMAGE
    && model.surfaces?.includes(ModelSurface.IMAGE_GENERATE)
  );
}

function getRepositoryImageModelById(modelId) {
  return MODEL_REPOSITORY.find((model) => (
    model.id === modelId
    && model.family === ModelFamily.IMAGE
  )) || null;
}

// Accept either repository entries or lightweight active-model records from settings state.
function resolveImageRepositoryModel(modelOrId) {
  if (typeof modelOrId === 'string') return getRepositoryImageModelById(modelOrId);
  if (isImageGenerateModel(modelOrId)) return modelOrId;
  if (modelOrId?.id) return getRepositoryImageModelById(modelOrId.id);
  return null;
}

function createImageModelViewModel(modelOrId) {
  const model = resolveImageRepositoryModel(modelOrId);
  if (!isImageGenerateModel(model)) return null;
  return mapRepositoryImageModel(model);
}

/**
 * Derive image picker models from repository entries.
 * Null builds the full catalog; an explicit empty array represents no active models.
 */
export function getImageModelViewModels(repositoryModels = null) {
  const sourceModels = Array.isArray(repositoryModels)
    ? repositoryModels
    : MODEL_REPOSITORY.filter(isImageGenerateModel);

  return sourceModels
    .map(createImageModelViewModel)
    .filter(Boolean);
}

export const IMAGE_MODELS = getImageModelViewModels();
const IMAGE_MODEL_BY_KEY = new Map();
const IMAGE_MODEL_BY_QUALITY = new Map();
const RUNTIME_VIEW_MODEL_CACHE = new WeakMap();

function setImageModelLookupKey(index, key, model) {
  if (!key) return;
  const existingModel = index.get(key);
  if (existingModel && existingModel !== model) {
    throw new Error(`Duplicate image model lookup key: ${key}`);
  }
  index.set(key, model);
}

for (const model of IMAGE_MODELS) {
  setImageModelLookupKey(IMAGE_MODEL_BY_KEY, model.key, model);
  setImageModelLookupKey(IMAGE_MODEL_BY_KEY, model.editKey, model);
  IMAGE_MODEL_BY_QUALITY.set(model.qualityTier, model);
}

function getRuntimeImageViewModel(repositoryModel) {
  if (!isImageGenerateModel(repositoryModel)) return null;
  const cached = RUNTIME_VIEW_MODEL_CACHE.get(repositoryModel);
  if (cached) return cached;
  const viewModel = mapRepositoryImageModel(repositoryModel);
  RUNTIME_VIEW_MODEL_CACHE.set(repositoryModel, viewModel);
  return viewModel;
}

function findRuntimeImageViewModel(predicate) {
  // Predicate matches against the canonical repository model so the static
  // built-ins keep using the prebuilt map (fast path) and runtime-only custom
  // models flow through the same view-model adapter.
  const repositoryModel = getRuntimeImageRepositoryModel(predicate);
  return repositoryModel ? getRuntimeImageViewModel(repositoryModel) : null;
}

function getRuntimeImageRepositoryModel(predicate) {
  const candidate = predicate();
  return isImageGenerateModel(candidate) ? candidate : null;
}

/**
 * Model-specific reference image layout strategies.
 *
 * Each model handles character/prop/environment/style references differently.
 * This function returns the ordering strategy and per-bucket limits so callers
 * (storyboard generation, agent tools) can assemble references correctly.
 *
 * @param {string} modelKey - Model key (generate or edit)
 * @returns {{ strategy: string, buckets: Object }} Layout descriptor
 */
export function getModelReferenceLayout(modelKey) {
  const repositoryModel = getRepositoryImageModel(modelKey);
  if (!repositoryModel) {
    return {
      strategy: 'flat',
      buckets: {},
      totalMax: 0,
    };
  }

  // A provider whose Engine declares its own reference slots answers FIRST.
  // The static definition is a fallback, not the truth: the Engine owns what
  // its recipes accept, and that can change without this file changing.
  const live = resolveLiveReferenceLayout(repositoryModel);
  if (live) return live;

  return deriveReferenceLayout(repositoryModel);
}

//: providerKey -> () => layout | null. Registered by the provider's own module
//: at init, so this config file never imports a service. Under plain Node
//: nothing registers and every lookup falls through to the static definition,
//: which is what keeps this module importable outside a browser.
const LIVE_REFERENCE_RESOLVERS = new Map();

export function registerLiveReferenceContractResolver(providerKey, resolver) {
  if (!providerKey) return;
  // Accepts either a bare resolve function or { resolve, hasAnswered }. The
  // second form exists so callers can tell "this model accepts nothing" apart
  // from "we have not been able to ask", without importing the provider.
  const entry = typeof resolver === 'function' ? { resolve: resolver } : resolver;
  if (typeof entry?.resolve !== 'function') return;
  LIVE_REFERENCE_RESOLVERS.set(providerKey, entry);
}

function resolveLiveReferenceLayout(repositoryModel) {
  const entry = LIVE_REFERENCE_RESOLVERS.get(repositoryModel?.provider);
  if (!entry) return null;
  try {
    return entry.resolve(repositoryModel.id) || null;
  } catch {
    // A resolver that throws must not take down model resolution. Falling
    // through to the static contract yields zero buckets and the honest
    // warning, which is the same place an unreachable Engine lands.
    return null;
  }
}

/** True when this model's provider can answer live — used to tell "does not
 *  accept" apart from "could not ask". */
export function hasLiveReferenceResolver(modelKey) {
  const repositoryModel = getRepositoryImageModel(modelKey);
  return LIVE_REFERENCE_RESOLVERS.has(repositoryModel?.provider);
}

/** True when a live-answering provider has actually answered this session.
 *  False here means "could not ask", which is NOT "accepts nothing". */
export function liveReferenceProviderHasAnswered(modelKey) {
  const repositoryModel = getRepositoryImageModel(modelKey);
  const entry = LIVE_REFERENCE_RESOLVERS.get(repositoryModel?.provider);
  if (!entry) return false;
  try {
    return entry.hasAnswered ? Boolean(entry.hasAnswered()) : true;
  } catch {
    return false;
  }
}

/**
 * The reference kinds a live declaration actually opened, or `[]`.
 *
 * The @mention dropdown filters assets against an accepted-kind Set, and the
 * static contract for a live-answering provider declares NONE — an empty Set
 * filters everything, so the user cannot open an asset list at all. This gives
 * the composer a live answer to pass through; a cloud provider has no resolver,
 * so it returns `[]` and callers keep their existing computation.
 */
export function getLiveReferenceAcceptedKinds(modelKey) {
  const live = resolveLiveReferenceLayout(getRepositoryImageModel(modelKey));
  if (!live?.buckets) return [];
  return Object.entries(live.buckets)
    .filter(([, bucket]) => (bucket?.max ?? 0) > 0)
    .map(([kind]) => kind);
}

/**
 * Ask a live-answering provider to refresh its declaration.
 *
 * The registry holds a REFERENCE to the provider's own single writer, so this
 * adds a caller and never a second writer. Under plain Node nothing registers,
 * the Map is empty, and this returns null without importing a service — which
 * is what keeps this module importable outside a browser.
 */
export async function refreshLiveReferenceContract(modelKey, options = {}) {
  const repositoryModel = getRepositoryImageModel(modelKey);
  const entry = LIVE_REFERENCE_RESOLVERS.get(repositoryModel?.provider);
  if (typeof entry?.refresh !== 'function') return null;
  return entry.refresh(options);
}

export { getSceneAssetReferenceBucketKind };

export function getModelReferenceBucketLimit(modelKey, kind) {
  const bucketKind = getSceneAssetReferenceBucketKind(kind);
  if (!bucketKind) return 0;
  return getModelReferenceLayout(modelKey)?.buckets?.[bucketKind]?.max ?? 0;
}

function normalizeReferenceBuckets(buckets = {}) {
  return Object.fromEntries(
    SCENE_ASSET_REFERENCE_BUCKET_KINDS.map((kind) => [kind, [...(buckets[kind] || [])]]),
  );
}

/**
 * Does this layout actually RESERVE capacity per kind, or is every bucket simply
 * allowed the whole budget?
 *
 * Most image models declare `character: total, prop: total, environment: total`,
 * which reserves nothing: the buckets become a priority ORDER and the first kind
 * eats the budget. Models with genuine caps (`nano-banana-2` declares 5 / 6 / 3)
 * are left exactly as they are -- their reservations already work.
 */
function layoutReservesPerKind(layout) {
  return Object.values(layout.buckets || {}).some((config) => config.max < layout.totalMax);
}

/**
 * Assemble reference image IDs for a model, from the ELEMENTS that contributed
 * them.
 *
 * Takes elements rather than pre-flattened kind buckets, because flattening
 * throws away which element an image came from and that is exactly what the
 * allocation needs to know. Buckets are still derived internally, so the model's
 * declared bucket caps and ordering keep working.
 *
 * TWO ALLOCATIONS, chosen by whether the model reserves anything and whether the
 * budget actually binds.
 *
 * A reserving layout keeps the original ordered concatenation, because its caps
 * already stop one kind starving another. So does any layout that does not need
 * to trim -- reference order can matter to a provider, so it is not moved
 * without cause.
 *
 * A flat layout that must trim is allocated by weighted fair queue PER ELEMENT.
 * Ordered concatenation there is depth-first by kind, and it was measured
 * dropping real references: two characters, a location and a style against
 * flux-2-pro's eight slots gave the characters all eight and the location and
 * the style ZERO. Per element it also fixes a quieter version of the same bug --
 * one character consuming the whole character bucket and leaving a SECOND
 * character with nothing.
 *
 * @param {Array<{kind: string, hero?: boolean, mediaIds: string[]}>} elements
 * @param {string} modelKey
 * @returns {string[]} Ordered, deduplicated reference image IDs
 */
export function assembleReferences(elements, modelKey) {
  const layout = getModelReferenceLayout(modelKey);
  const elementList = (Array.isArray(elements) ? elements : []).filter(Boolean);
  const normalizedBuckets = normalizeReferenceBuckets(
    elementList.reduce((buckets, element) => {
      const bucketKind = getSceneAssetReferenceBucketKind(element?.kind);
      if (!bucketKind) return buckets;
      buckets[bucketKind] = [...(buckets[bucketKind] || []), ...(element?.mediaIds || [])];
      return buckets;
    }, {}),
  );
  const sortedTypes = Object.entries(layout.buckets)
    .sort(([, a], [, b]) => a.order - b.order);

  const seen = new Set();
  const result = [];

  const availableCount = sortedTypes.reduce(
    (total, [type, config]) => total + Math.min((normalizedBuckets[type] || []).length, config.max),
    0,
  );

  // Nothing has to be dropped, so nothing has to be rebalanced. Keeping the
  // model's declared order here means this change is invisible except in the one
  // situation it exists for -- and reference ORDER can matter to a provider, so
  // not moving it without cause is the conservative read.
  const mustTrim = availableCount > layout.totalMax;

  if (layoutReservesPerKind(layout) || !mustTrim) {
    for (const [type, config] of sortedTypes) {
      const ids = (normalizedBuckets[type] || []).slice(0, config.max);
      for (const id of ids) {
        if (!seen.has(id)) {
          seen.add(id);
          result.push(id);
        }
      }
    }

    return result.slice(0, layout.totalMax);
  }

  // One queue PER ELEMENT, ordered by the model's declared bucket order first so
  // ties still resolve the way the model asked, then by the caller's order.
  // `emitted / weight` stamps: everyone's first image is stamped 0, so no element
  // present is dropped; heavier elements advance more slowly and emit more often;
  // an exhausted queue stops competing and its share goes to the rest.
  const bucketOrder = new Map(sortedTypes.map(([type], index) => [type, index]));
  const queues = elementList
    .map((element, index) => {
      const bucketKind = getSceneAssetReferenceBucketKind(element?.kind);
      const cap = bucketKind ? layout.buckets[bucketKind]?.max ?? 0 : 0;
      return {
        items: bucketKind ? (element?.mediaIds || []).slice(0, cap) : [],
        weight: getElementReferenceWeight(element),
        order: [bucketOrder.get(bucketKind) ?? Number.MAX_SAFE_INTEGER, index],
        emitted: 0,
      };
    })
    .filter((queue) => queue.items.length > 0)
    .sort((a, b) => (a.order[0] - b.order[0]) || (a.order[1] - b.order[1]));

  const totalAvailable = queues.reduce((total, queue) => total + queue.items.length, 0);
  for (let placed = 0; placed < totalAvailable; placed += 1) {
    let chosen = null;
    let chosenStamp = Infinity;
    for (const queue of queues) {
      if (queue.emitted >= queue.items.length) continue;
      const stamp = queue.emitted / queue.weight;
      if (stamp < chosenStamp) {
        chosen = queue;
        chosenStamp = stamp;
      }
    }
    if (!chosen) break;
    const id = chosen.items[chosen.emitted];
    chosen.emitted += 1;
    if (seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }

  return result.slice(0, layout.totalMax);
}

function getRepositoryImageModel(key) {
  const model = getImageModel(key);
  if (!model) return null;
  const fromStatic = MODEL_REPOSITORY.find((entry) => entry.id === model.key);
  if (fromStatic) return fromStatic;
  const runtimeModel = getModelById(model.key);
  return isImageGenerateModel(runtimeModel) ? runtimeModel : null;
}

/**
 * Get the preferred model key for an element based on media type.
 *
 * Falls back to null if the element has no preference for the given media type,
 * letting the caller use its own default (quality-tier logic, user selection, etc.).
 *
 * @param {Object} element - Element with optional preferredModels field
 * @param {'image'|'video'|'3d'} mediaType
 * @returns {string | null} Model key or null
 */
export function getPreferredModelForElement(element, mediaType) {
  const key = element?.defaults?.[
    mediaType === 'image'
      ? 'imageModelKey'
      : mediaType === 'video'
        ? 'videoModelKey'
        : 'threeDModelKey'
  ] || null;
  if (!key) return null;
  if (mediaType === 'image' && !getImageModel(key)) return null;
  return key;
}

const CUSTOM_QUALITY_PREFIX = 'custom:';

/** Look up by generate key OR edit key. Returns the model entry (not a copy). */
export function getImageModel(key) {
  const builtin = IMAGE_MODEL_BY_KEY.get(key);
  if (builtin) return builtin;
  if (typeof key !== 'string' || !key) return null;
  return findRuntimeImageViewModel(() => getModelById(key));
}

/**
 * Look up by quality tier. Built-in tiers include 'stylized', 'standard',
 * 'design', 'reasoning', 'structured', 'enhanced', 'premium', 'character',
 * 'props'. Custom catalog models use `custom:<id>` so each one has a unique
 * selector identity.
 */
export function findImageModelByQuality(quality) {
  const builtin = IMAGE_MODEL_BY_QUALITY.get(quality);
  if (builtin) return builtin;
  if (typeof quality !== 'string' || !quality.startsWith(CUSTOM_QUALITY_PREFIX)) {
    return null;
  }
  const customId = quality.slice(CUSTOM_QUALITY_PREFIX.length);
  return findRuntimeImageViewModel(() => getModelById(customId));
}

export function getDefaultImageModel() {
  return IMAGE_MODEL_BY_KEY.get(DEFAULT_IMAGE_MODEL_KEY) ?? IMAGE_MODELS[0] ?? null;
}

export function getDefaultImageQuality() {
  return getDefaultImageModel()?.qualityTier || 'standard';
}

export function getDefaultImageEnhancementKey() {
  const defaultModel = getDefaultImageModel();
  return defaultModel?.enhancementKey || defaultModel?.key || null;
}

export function getImageModelByQuality(quality) {
  return findImageModelByQuality(quality) ?? getDefaultImageModel();
}

/** Get the effective model key based on whether images are attached and quality tier. */
export function getEffectiveModelKey(hasImages, quality) {
  const model = getImageModelByQuality(quality);
  if (!hasImages) return model.key;
  return model.attachedImageMode === 'edit' ? model.editKey : model.key;
}

/** Determine if the given key is an edit variant. */
export function isEditMode(key) {
  const model = getImageModel(key);
  return model ? key === model.editKey : false;
}

/** Get the max number of prompt/reference images the model accepts.
 *
 * The live declaration wins, on the same rule as `getModelReferenceLayout`.
 * Without this consult a live-capable model reads its STATIC zero, and
 * `getTotalPromptImageLimit`'s `min()` then strips every attached reference
 * downstream — silently for subject and scene roles.
 */
export function getMaxInputImages(modelKey) {
  const model = getImageModel(modelKey);
  if (!model) return 0;
  const live = resolveLiveReferenceLayout(getRepositoryImageModel(modelKey));
  if (live && Number.isFinite(live.totalMax)) return live.totalMax;
  const mode = isEditMode(modelKey) ? 'edit' : 'generate';
  return model.capabilities.maxInputImages?.[mode] ?? 0;
}

/**
 * How many reference images the user may ATTACH to this model, across both of its routes.
 *
 * A different question from `getMaxInputImages`, which answers for one route at a time. A
 * text-to-image model reports `generate: 0` while its image-to-image route accepts many, and
 * attaching a reference is precisely what switches it to that route. Asking the generate
 * route alone makes the FIRST attachment impossible, so the switch can never happen. The
 * composer's picker enforced exactly that and silently swallowed every click for ten of the
 * thirteen image models — measured: nano-banana-2 0/14, gpt-image-2.5 0/16, seedream-4.5 0/10.
 *
 * `max` of the two rather than "just use the edit route", because midjourney is the mirror
 * case: generate 4, edit 0.
 */
export function getMaxReferenceImages(modelKey) {
  const model = getImageModel(modelKey);
  if (!model) return 0;
  // A live declaration wins here for the same reason it wins in
  // getModelReferenceLayout: a provider that answers at runtime knows its own
  // capacity, and its STATIC entry declares zero. Without this consult the
  // attach gate reads 0 for every local model and the picker swallows the
  // click -- the same defect this function was written to fix for cloud.
  const live = resolveLiveReferenceLayout(getRepositoryImageModel(modelKey));
  if (live && Number.isFinite(live.totalMax)) return live.totalMax;
  const limits = model.capabilities.maxInputImages || {};
  return Math.max(limits.generate ?? 0, limits.edit ?? 0);
}

/** Get the max number of output images the model accepts. */
export function getMaxOutputImages(modelKey) {
  const model = getImageModel(modelKey);
  if (!model) return 1;
  const mode = isEditMode(modelKey) ? 'edit' : 'generate';
  return model.capabilities.maxOutputImages?.[mode] ?? 1;
}

/**
 * Get the min number of output images the model produces per request. A model
 * with a fixed batch (Midjourney's 4-image grid) returns that many no matter
 * what count is asked for, so callers must reserve at least this many slots.
 */
export function getMinOutputImages(modelKey) {
  const model = getImageModel(modelKey);
  if (!model) return 1;
  const mode = isEditMode(modelKey) ? 'edit' : 'generate';
  return model.capabilities.minOutputImages?.[mode] ?? 1;
}

/** Determine whether the selected model requires prompt/reference images. */
export function requiresPromptImages(modelKey) {
  const model = getImageModel(modelKey);
  return !!model?.requiresPromptImages;
}

/**
 * Models with explicit character-reference support, or with a structured
 * reference layout beyond the generic flat edit path, can preserve identity.
 */
export function supportsCharacterReferences(modelKey) {
  const model = getImageModel(modelKey);
  if (model?.supportsCharacterReferences) return true;
  return getModelReferenceLayout(modelKey).strategy !== 'flat';
}

/** INTERIM until P4's directability axis; swap the read, keep the name. */
export function supportsReferenceNarration(modelKey) {
  return Boolean(getImageModel(modelKey)?.capabilities?.supportsReferenceNarration);
}

/**
 * Prefer a dedicated reference-first model when nudging users away from
 * flat edit models for character consistency.
 */
export function getCharacterReferenceUpgradeModelKey() {
  const preferredKeys = [
    'nano-banana-2-flash',
    'nano-banana-2',
    'kling-o1-image',
    'ideogram-character',
  ];
  const preferred = preferredKeys
    .map((key) => IMAGE_MODELS.find((model) => model.key === key))
    .find((model) => model?.supportsCharacterReferences);
  if (preferred) return preferred.key;
  const fallback = IMAGE_MODELS.find((model) => model.supportsCharacterReferences);
  return fallback?.editKey || fallback?.key || null;
}

/** Get display name with optional ref/style suffix. */
export function getImageModelDisplayName(modelKey, refCount, styleCount) {
  const model = getImageModel(modelKey);
  if (!model) return 'Unknown';
  const suffix = [
    refCount > 0 ? `${refCount} ref` : '',
    styleCount > 0 ? `${styleCount} style` : '',
  ].filter(Boolean).join(', ');
  if (isEditMode(modelKey)) return `${model.label} (${suffix || 'edit'})`;
  return suffix ? `${model.label} (${suffix})` : model.label;
}

/**
 * Build model-specific metadata extras for generation history.
 *
 * Preserves per-model metadata shape:
 *   grok (both):        { aspectRatio, revisedPrompt }
 *   flux (both):        { aspectRatio, resolution, width, height }
 *   nano (both):        { aspectRatio, resolution, width, height }
 *   gpt-image-2.5:        { aspectRatio, width, height }
 */
export function buildMetadataExtras(modelKey, params, imageData, ASPECT_RATIOS, getScaledDimensions) {
  const model = getImageModel(modelKey);
  if (!model) return {};
  const extras = { aspectRatio: params.aspectRatio };

  if (model.capabilities.resolutions && getScaledDimensions) {
    extras.resolution = params.resolution;
    const dims = getScaledDimensions(params.aspectRatio, params.resolution);
    extras.width = dims.width;
    extras.height = dims.height;
  } else if (model.key === 'flux-2-pro' && !isEditMode(modelKey) && ASPECT_RATIOS?.[params.aspectRatio]) {
    const dims = ASPECT_RATIOS[params.aspectRatio];
    extras.width = dims.width;
    extras.height = dims.height;
  } else if (model.key === 'gpt-image-2.5' && ASPECT_RATIOS?.[params.aspectRatio]) {
    const dims = ASPECT_RATIOS[params.aspectRatio];
    extras.width = imageData?.width || dims.width;
    extras.height = imageData?.height || dims.height;
  }

  if (imageData?.revisedPrompt) {
    extras.revisedPrompt = imageData.revisedPrompt;
  }

  // Persist Midjourney parent-job provenance into generationParams so a later
  // "more like this" can route to a native MJ variation (POST /action needs
  // the parent jobId + grid index). Transient result metadata does not survive
  // reload — only generationParams does.
  if (model.provider === 'midjourney' && imageData?.metadata) {
    const { midjourneyParentJobId, midjourneyGridIndex } = imageData.metadata;
    if (midjourneyParentJobId) extras.midjourneyParentJobId = midjourneyParentJobId;
    if (Number.isInteger(midjourneyGridIndex)) extras.midjourneyGridIndex = midjourneyGridIndex;
  }
  return extras;
}

function getImageAdapterKey(model, mode) {
  const baseAdapter = model?.creativeAdapter || 'image-text-basic';
  if (mode === 'generate') {
    return baseAdapter;
  }
  if (baseAdapter === 'image-reference-single-character') {
    return baseAdapter;
  }
  if (baseAdapter === 'image-reference-positional') {
    return baseAdapter;
  }
  if (model?.key === 'nano-banana-2' || model?.key === 'nano-banana-2-flash') {
    return 'image-edit-bucketed';
  }
  return 'image-edit-flat';
}

export function getImageCreativeProfile(modelKey, mode = 'generate') {
  const model = getImageModel(modelKey);
  if (!model) return null;
  const requestedMode = mode === 'reference' ? 'edit' : mode;
  const capabilityMode = requestedMode === 'edit' ? 'edit' : 'generate';

  return {
    modelKey,
    family: 'image',
    requestMode: requestedMode,
    adapterKey: getImageAdapterKey(model, requestedMode),
    // Live OR static: a live-capable model must not report itself unable to
    // take prompt images just because its static contract declares none.
    supportsPromptImages: (model.capabilities.maxInputImages?.[capabilityMode] || 0) > 0
      || getLiveReferenceAcceptedKinds(modelKey).length > 0,
    supportsNegativePrompt: Boolean(model.capabilities.hasNegativePrompt),
    supportsGuidanceScale: Boolean(model.capabilities.hasGuidanceScale),
    supportsInferenceSteps: Boolean(model.capabilities.hasInferenceSteps),
    supportsResolution: Array.isArray(model.capabilities.resolutions) && model.capabilities.resolutions.length > 0,
    supportsAspectRatio: Array.isArray(model.capabilities.aspectRatios) && model.capabilities.aspectRatios.length > 0,
    supportsBoardPresets: Boolean(model.capabilities.supportsBoardPresets),
    supportsBatchSelection: Boolean(model.capabilities.supportsBatchSelection),
    hasNumericDials: Boolean(model.capabilities.numericDials),
    supportsAudio: false,
    supportsLastFrame: false,
    supportsMultiPrompt: false,
    supportsNativeElements: false,
    promptBudget: model.key === 'kling-o1-image'
      ? 'short'
      : (model.key === 'nano-banana-2' || model.key === 'nano-banana-2-flash' ? 'medium' : 'long'),
    prefersConcisePrompt: model.key === 'kling-o1-image' || model.key === 'ideogram-character',
    maxInputImages: model.capabilities.maxInputImages?.[capabilityMode] ?? 0,
    maxOutputImages: model.capabilities.maxOutputImages?.[capabilityMode] ?? 1,
    claimedDimensionsFromImages: ['subject', 'scene'],
  };
}

export function getModelPromptCharBudget(modelKey) {
  const profile = getImageCreativeProfile(modelKey);
  return profile
    ? promptCharBudgetFor(profile.promptBudget, profile.prefersConcisePrompt)
    : null;
}
